import { getAuth } from 'firebase/auth'
import store from '@/store'

const API_URL = import.meta.env.VITE_API_URL

interface ApiConfig {
	timeout?: number
	headers?: Record<string, string>
	responseType?: string
	redirect?: RequestRedirect
}

class ApiService {
	private async getAuthToken(): Promise<string> {
		const auth = getAuth()
		const token = await auth.currentUser?.getIdToken()
		if (!token) {
			throw new Error('No authentication token available')
		}
		return token
	}

	private async request<T>(method: string, path: string, params?: any, customConfig?: ApiConfig): Promise<T> {
		// Activar el loader antes de la petición
		store.dispatch('loader/setLoading', { isLoading: true })

		const config: ApiConfig = {
			timeout: 120000, // 2 minutes default timeout
			headers: {
				'Content-Type': 'application/json; charset=UTF-8',
				'Accept': 'application/json, text/plain, */*'
			}
		}

		// Apply custom config if provided
		if (customConfig) {
			Object.assign(config, customConfig)
		}

		// Add auth token to headers
		try {
			const token = await this.getAuthToken()
			config.headers = {
				...config.headers,
				'Authorization': `${token}`
			}
		} catch (error) {
			console.error('Error getting auth token:', error)
			throw error
		}

		// Build full URL - ensure proper URL construction
		const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL
		const cleanPath = path.startsWith('/') ? path : `/${path}`
		const url = path.startsWith('http') ? path : `${baseUrl}${cleanPath}`

		// Prepare request options
		const requestOptions: RequestInit = {
			method,
			headers: config.headers,
			mode: 'cors', // Explicitly set CORS mode
			redirect: config.redirect
		}

		// Add body for POST/PUT requests
		if (params && (method === 'POST' || method === 'PUT')) {
			if (params instanceof FormData) {
				// For FormData, let the browser set the Content-Type
				delete config.headers['Content-Type']
				requestOptions.body = params
			} else {
				requestOptions.body = JSON.stringify(params)
			}
		}

		// Make the request
		try {
			const response = await fetch(url, requestOptions)

			if (!response.ok) {
				// Intentar obtener el mensaje de error de forma segura
				let errorMessage = `HTTP error! status: ${response.status}`

				try {
					const contentType = response.headers.get('content-type')
					if (contentType && contentType.includes('application/json')) {
						const errorData = await response.json()
						errorMessage = errorData.message || errorData.error || errorMessage
					} else {
						// Si no es JSON, leer como texto
						const errorText = await response.text()
						errorMessage = errorText?.trim() ? errorText : errorMessage
					}
				} catch {
					// Si falla el parseo, intentar leer como texto
					try {
						const errorText = await response.text()
						errorMessage = errorText?.trim() ? errorText : errorMessage
					} catch (textError) {
						// Si también falla, usar el mensaje por defecto
						console.error('Error al leer respuesta de error:', textError)
					}
				}

				throw new Error(errorMessage)
			}
			// Handle response based on responseType
			let result: T
			if (config.responseType === 'arraybuffer') {
				const arrayBuffer = await response.arrayBuffer()
				result = arrayBuffer as unknown as T
			} else if (config.responseType === 'blob') {
				const blob = await response.blob()
				result = blob as unknown as T
			} else if (response.headers.get('content-type')?.includes('application/json')) {
				let data
				try {
					data = await response.json()
					if (data.message) {
						store.dispatch('notification/showSuccess', data.message || 'Operación exitosa')
					}
				} catch (error) {
					console.warn('Error al procesar la respuesta del servidor:', error)
				}
				result = data as T
			} else {
				result = response.text() as unknown as T
			}

			// Desactivar el loader después de recibir la respuesta
			store.dispatch('loader/setLoading', { isLoading: false })
			return result
		} catch (error) {
			// Desactivar el loader en caso de error
			store.dispatch('loader/setLoading', { isLoading: false })
			const errorMessage = (error as Error)?.message || 'Error al enviar el paquete a la SEP'
			store.dispatch('notification/showError', errorMessage)
			console.error(`API request error (${method} ${path}):`, error)
			throw error
		}
	}

	async post<T = any>(path: string, params?: any, config?: ApiConfig): Promise<T> {
		return this.request<T>('POST', path, params, config)
	}

	async put<T = any>(path: string, params?: any, config?: ApiConfig): Promise<T> {
		return this.request<T>('PUT', path, params, config)
	}

	async get<T = any>(path: string, config?: ApiConfig): Promise<T> {
		return this.request<T>('GET', path, undefined, config)
	}

	async delete<T = any>(path: string, config?: ApiConfig): Promise<T> {
		return this.request<T>('DELETE', path, undefined, config)
	}

	async callAuth<T = any>(method: string, path: string, params?: any, customConfig?: ApiConfig): Promise<T> {
		const auth = getAuth()
		const idToken = await auth.currentUser?.getIdToken()
		if (!idToken) {
			throw new Error('No authentication token available')
		}

		const config: ApiConfig = {
			timeout: 120000,
			headers: {
				Authorization: idToken
			}
		}

		if (customConfig) {
			Object.assign(config, customConfig)
		}

		return this.request<T>(method, path, params, config)
	}
}

export const apiService = new ApiService()
